"use client";

// Le retour de Stripe après un achat à vie.
//
// On ne peut pas laisser quelqu'un qui vient de payer 149 € retomber sur la
// fiche avec une notification qui s'efface au bout de trois secondes : il
// doit voir que c'est fait, et savoir ce qui sera prélevé ensuite s'il a
// choisi le paiement en trois fois.
//
// Même fenêtre que l'achat (BottomSheetModal) pour rester dans la même
// famille visuelle, confettis comme les quêtes, et une animation sobre :
// fondu, seize pixels de montée, trois cents millisecondes.

import { useEffect, useState } from "react";
import { motion } from "motion/react";
import ConfettiExplosion from "react-confetti-explosion";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import {
    formatDayMonth,
    formatEuros,
    installmentAmounts,
    installmentDates,
} from "@/lib/courseLifetimePricing";

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {object} props.course la formation achetée (slug, name, image)
 * @param {number} props.price prix payé, en euros
 * @param {string} props.plan "full" ou "installments"
 * @param {string} props.state "confirming" tant que le serveur ouvre l'accès,
 *   "ready" une fois ouvert, "pending" si le webhook est en retard
 */
export default function CoursePurchaseCelebration({
    isOpen,
    onClose,
    course,
    price,
    plan,
    state = "ready",
}) {
    const t = useTranslations();
    const locale = useLocale();
    const router = useRouter();
    const [confetti, setConfetti] = useState(false);

    // Stripe ne renvoie ici qu'après un paiement réussi : les confettis
    // partent sans attendre l'ouverture de l'accès. Un léger retard, le temps
    // que la fenêtre soit à l'écran, sinon ils se joueraient derrière.
    const pending = state === "pending";
    useEffect(() => {
        if (!isOpen || pending) return;
        const timer = setTimeout(() => setConfetti(true), 250);
        return () => clearTimeout(timer);
    }, [isOpen, pending]);

    const amounts = installmentAmounts(price);
    const [secondDate, thirdDate] = installmentDates();

    const start = () => {
        // On ne vide pas l'écran avant que la page suivante soit demandée.
        router.push(`/course/${course.slug}/1/1?start=1`);
    };

    return (
        <BottomSheetModal
            isOpen={isOpen}
            onClose={onClose}
            labelledBy="course-purchase-celebration-title"
            maxWidthClass="sm:max-w-md"
            portal
        >
            <div className="p-6 text-center font-geistSans sm:p-8">
                {confetti && (
                    <div className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2">
                        <ConfettiExplosion
                            duration={2600}
                            particleCount={70}
                            width={520}
                            zIndex={10001}
                        />
                    </div>
                )}

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-excitationOrange/10"
                >
                    <SolarIcon
                        name={pending ? "clock-circle-linear" : "check-circle-bold"}
                        className="text-3xl text-excitationOrange"
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: "easeOut", delay: 0.05 }}
                >
                    <h2
                        id="course-purchase-celebration-title"
                        className="mb-0 mt-5 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"
                    >
                        {t(
                            pending
                                ? "course_access_celebration_pending_title"
                                : "course_access_celebration_title",
                        )}
                    </h2>
                    <p className="mb-0 mt-2 text-[15px] font-medium text-gray-900 dark:text-white">
                        {course.name}
                    </p>
                    <p className="mb-0 mt-3 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {pending
                            ? t("course_access_celebration_pending_text")
                            : plan === "installments"
                              ? t("course_access_celebration_installments", {
                                    second: formatEuros(amounts[1], locale),
                                    secondDate: formatDayMonth(secondDate, locale),
                                    third: formatEuros(amounts[2], locale),
                                    thirdDate: formatDayMonth(thirdDate, locale),
                                })
                              : t("course_access_celebration_full")}
                    </p>

                    <div className="mt-6">
                        {/* Tant que le serveur n'a pas ouvert l'accès, le
                            bouton tourne : cliquer trop tôt renverrait sur la
                            fiche, faute de droit. */}
                        <Button
                            type="primary"
                            block
                            onClick={start}
                            isLoading={state === "confirming"}
                            progress
                        >
                            {t("course_access_celebration_cta")}
                        </Button>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="mt-3 text-sm text-gray-500 underline underline-offset-4 transition-colors hover:text-gray-900 dark:text-grayDark-400 dark:hover:text-white"
                    >
                        {t("course_access_celebration_later")}
                    </button>
                </motion.div>
            </div>
        </BottomSheetModal>
    );
}
